import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  FiBook, FiArrowLeft, FiCalendar, FiUser, FiLayers, FiGlobe,
  FiHome, FiLogIn, FiExternalLink, FiBookOpen, FiInfo, FiHash,
  FiMapPin, FiPackage, FiFileText
} from 'react-icons/fi';
import './BookDetailPublic.css';

const API_BASE = '/api';

const BookDetailPublic = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBook = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE}/public_books.php?id=${id}`);
        const data = await response.json();
        
        if (data.success) {
          setBook(data.data);
        } else {
          setError(data.message || 'Buku tidak ditemukan');
        }
      } catch (err) {
        setError('Gagal memuat data buku');
        console.error('Error fetching book:', err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchBook();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="book-detail-public">
        <nav className="book-detail-public__nav">
          <div className="book-detail-public__nav-container">
            <Link to="/" className="book-detail-public__logo">
              <FiBook className="book-detail-public__logo-icon" />
              <span>STIKBAR E-Library</span>
            </Link>
            <div className="book-detail-public__nav-actions">
              <Link to="/" className="book-detail-public__nav-btn">
                <FiHome /> Beranda
              </Link>
              <Link to="/login" className="book-detail-public__nav-btn book-detail-public__nav-btn--primary">
                <FiLogIn /> Masuk
              </Link>
            </div>
          </div>
        </nav>
        <main className="book-detail-public__main">
          <div className="book-detail-public__loading">
            <div className="book-detail-public__loading-spinner"></div>
            <p>Memuat data buku...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="book-detail-public">
        <nav className="book-detail-public__nav">
          <div className="book-detail-public__nav-container">
            <Link to="/" className="book-detail-public__logo">
              <FiBook className="book-detail-public__logo-icon" />
              <span>STIKBAR E-Library</span>
            </Link>
            <div className="book-detail-public__nav-actions">
              <Link to="/" className="book-detail-public__nav-btn">
                <FiHome /> Beranda
              </Link>
              <Link to="/login" className="book-detail-public__nav-btn book-detail-public__nav-btn--primary">
                <FiLogIn /> Masuk
              </Link>
            </div>
          </div>
        </nav>
        <main className="book-detail-public__main">
          <div className="book-detail-public__error">
            <FiInfo className="book-detail-public__error-icon" />
            <h2>Buku Tidak Ditemukan</h2>
            <p>{error}</p>
            <Link to="/katalog-publik" className="book-detail-public__error-btn">
              <FiArrowLeft /> Kembali ke Katalog
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const isExternal = book.tipe_buku === 'external' || book.link_external;
  const isReadable = book.is_readable === 1 || book.is_readable === '1';

  return (
    <div className="book-detail-public">
      {/* Navigation */}
      <nav className="book-detail-public__nav">
        <div className="book-detail-public__nav-container">
          <Link to="/" className="book-detail-public__logo">
            <FiBook className="book-detail-public__logo-icon" />
            <span>STIKBAR E-Library</span>
          </Link>
          <div className="book-detail-public__nav-actions">
            <Link to="/" className="book-detail-public__nav-btn">
              <FiHome /> Beranda
            </Link>
            <Link to="/login" className="book-detail-public__nav-btn book-detail-public__nav-btn--primary">
              <FiLogIn /> Masuk
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="book-detail-public__main">
        <div className="book-detail-public__container">
          {/* Breadcrumb */}
          <div className="book-detail-public__breadcrumb">
            <Link to="/katalog-publik">
              <FiArrowLeft /> Kembali ke Katalog
            </Link>
          </div>

          {/* Book Content */}
          <div className="book-detail-public__content">
            {/* Cover Section */}
            <div className="book-detail-public__cover-section">
              <div className="book-detail-public__cover">
                <img 
                  src={book.cover_image || '/images/cover-default.png'} 
                  alt={book.judul}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/images/cover-default.png';
                  }}
                />
                {isExternal ? (
                  isReadable ? (
                    <span className="book-detail-public__badge book-detail-public__badge--free">
                      <FiGlobe /> Akses Gratis
                    </span>
                  ) : (
                    <span className="book-detail-public__badge book-detail-public__badge--external">
                      <FiExternalLink /> Open Library
                    </span>
                  )
                ) : parseInt(book.stok_tersedia) > 0 ? (
                  <span className="book-detail-public__badge book-detail-public__badge--available">
                    Tersedia
                  </span>
                ) : (
                  <span className="book-detail-public__badge book-detail-public__badge--borrowed">
                    Dipinjam
                  </span>
                )}
              </div>

              {/* Action Buttons */}
              <div className="book-detail-public__actions">
                {isExternal && book.link_external && (
                  <a 
                    href={book.link_external} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="book-detail-public__action-btn book-detail-public__action-btn--primary"
                  >
                    <FiExternalLink /> {isReadable ? 'Baca Gratis' : 'Lihat di Open Library'}
                  </a>
                )}
                <Link to="/login" className="book-detail-public__action-btn book-detail-public__action-btn--secondary">
                  <FiLogIn /> Login untuk Meminjam
                </Link>
              </div>
            </div>

            {/* Info Section */}
            <div className="book-detail-public__info-section">
              <h1 className="book-detail-public__title">{book.judul}</h1>
              
              <div className="book-detail-public__author">
                <FiUser /> {book.penulis || 'Unknown'}
              </div>

              {/* Quick Info */}
              <div className="book-detail-public__quick-info">
                {book.nama_kategori && (
                  <span className="book-detail-public__category">
                    <FiLayers /> {book.nama_kategori}
                  </span>
                )}
                {book.tahun_terbit && (
                  <span className="book-detail-public__year">
                    <FiCalendar /> {book.tahun_terbit}
                  </span>
                )}
              </div>

              {/* Description */}
              {book.deskripsi && (
                <div className="book-detail-public__description">
                  <h3><FiFileText /> Deskripsi</h3>
                  <p>{book.deskripsi}</p>
                </div>
              )}

              {/* Details Grid */}
              <div className="book-detail-public__details">
                <h3><FiInfo /> Informasi Detail</h3>
                <div className="book-detail-public__details-grid">
                  {book.isbn && (
                    <div className="book-detail-public__detail-item">
                      <span className="book-detail-public__detail-label">
                        <FiHash /> ISBN
                      </span>
                      <span className="book-detail-public__detail-value">{book.isbn}</span>
                    </div>
                  )}
                  {book.nama_penerbit && (
                    <div className="book-detail-public__detail-item">
                      <span className="book-detail-public__detail-label">
                        <FiBookOpen /> Penerbit
                      </span>
                      <span className="book-detail-public__detail-value">{book.nama_penerbit}</span>
                    </div>
                  )}
                  {book.edisi && (
                    <div className="book-detail-public__detail-item">
                      <span className="book-detail-public__detail-label">
                        <FiLayers /> Edisi
                      </span>
                      <span className="book-detail-public__detail-value">{book.edisi}</span>
                    </div>
                  )}
                  {book.bahasa && (
                    <div className="book-detail-public__detail-item">
                      <span className="book-detail-public__detail-label">
                        <FiGlobe /> Bahasa
                      </span>
                      <span className="book-detail-public__detail-value">{book.bahasa}</span>
                    </div>
                  )}
                  {book.jumlah_halaman && (
                    <div className="book-detail-public__detail-item">
                      <span className="book-detail-public__detail-label">
                        <FiFileText /> Halaman
                      </span>
                      <span className="book-detail-public__detail-value">{book.jumlah_halaman} halaman</span>
                    </div>
                  )}
                  {!isExternal && (
                    <div className="book-detail-public__detail-item">
                      <span className="book-detail-public__detail-label">
                        <FiPackage /> Stok
                      </span>
                      <span className="book-detail-public__detail-value">
                        {book.stok_tersedia} / {book.stok_total} tersedia
                      </span>
                    </div>
                  )}
                  {book.nama_rak && (
                    <div className="book-detail-public__detail-item">
                      <span className="book-detail-public__detail-label">
                        <FiMapPin /> Lokasi Rak
                      </span>
                      <span className="book-detail-public__detail-value">
                        {book.nama_rak} {book.lokasi_rak && `(${book.lokasi_rak})`}
                      </span>
                    </div>
                  )}
                  {book.sumber_buku && (
                    <div className="book-detail-public__detail-item">
                      <span className="book-detail-public__detail-label">
                        <FiInfo /> Sumber
                      </span>
                      <span className="book-detail-public__detail-value">{book.sumber_buku}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Login CTA */}
          <div className="book-detail-public__cta">
            <div className="book-detail-public__cta-content">
              <h3>Ingin meminjam buku ini?</h3>
              <p>Silakan login untuk dapat meminjam buku dari perpustakaan STIKBAR</p>
            </div>
            <Link to="/login" className="book-detail-public__cta-btn">
              <FiLogIn /> Login Sekarang
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="book-detail-public__footer">
        <div className="book-detail-public__footer-container">
          <p>&copy; 2024 STIKBAR E-Library. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BookDetailPublic;
